// @flow
import * as React from 'react';
import { hasSubmitSucceeded } from 'redux-form';

import { makeTheme, withTheme, withStyles } from '@phg/stilo-toolbox/components/ThemeProvider';
import { Link } from '@phg/stilo-toolbox/v2/component';
import Theme from '@phg/stilo-toolbox/v2/theme';

import Error from 'app/components/errorComponent';
import Login from 'app/components/loginComponent';
import EmailConfirm from 'app/components/emailConfirmComponent';
import {l} from 'app/Translate';
import connectWithFormName from 'app/hoc/withFormName';

import makeStyle from './style';
import { WepPageSupportedLanguages } from './constant';
import LanguageSelector from './component/LanguageSelector';
import PartnerizeLogo from './component/PartnerizeLogo';
import Stepper from './component/Stepper';
import PageHeightAdjuster from './component/PageHeightAdjuster';

// Steps
import AddressComponent from './steps/AddressComponent';
import UserComponent from './steps/UserComponent';
import CompanyComponent from './steps/CompanyComponent';

type CustomisationProps = {
    classes: {
        Onboarding: string,
        SignupForm: string,
        Bar: string,
        Logo: string,
        BigLogo: string,
        Header: string,
        Footer: string,
        Logo: string,
        ContentPanel: string,
        BigError: string
    },
    fatalErrorOccurred: boolean,
    locale: string,
    networkId: string,
    networkName: string,
    signUpCompleteUrl: string,
    logoUrl: ?string,
    welcomeMessage: ?string,
    isAvailable?: boolean,
    confirmEmail?: boolean,
    registrationCompleted?: boolean,
    apiUrl?: string,
    apiProtocol?: string,
    captchaProvider?: string
};

// eslint-disable-next-line complexity
const Customisation = ({ classes, fatalErrorOccurred, networkId, locale, networkName, signUpCompleteUrl, logoUrl,
    welcomeMessage, isAvailable, confirmEmail, registrationCompleted, apiUrl, apiProtocol, captchaProvider }: CustomisationProps) => {
    document.title = l('Sign up to :name', { name: networkName });

    if (!isAvailable) {
        return (
            <div className={classes.BigError}>
                <Error error={l('URL is now invalid')} />
            </div>
        );
    }

    const merchantId = sessionStorage.getItem('merchantId');
    const loginUrl = signUpCompleteUrl + '/login/' + networkName + '/' + locale;
    const contents = fatalErrorOccurred ? <Error/> : (
        <React.Fragment>
            {(!registrationCompleted || !confirmEmail) ? (
                <div className={classes.Onboarding}>
                    {logoUrl ? (
                        <div className={classes.BigLogo}>
                            <img id="network-logo" alt={`${networkName} Logo`} src={logoUrl} />
                        </div>
                    ) : (
                        <PartnerizeLogo id="partnerize-logo" width={300} className={classes.BigLogo} />
                    )}
                    <div className={classes.Header} id="form-title">
                        {welcomeMessage ? welcomeMessage : l('Sign up to :name', { name: networkName })}
                    </div>
                    <Stepper id="stepper" labels={[l('Contact'), l('Address'), l('Extras & terms')]}>
                        <UserComponent networkName={networkName} merchantId={merchantId}/>
                        <AddressComponent />
                        <CompanyComponent networkId={networkId} captchaProvider={captchaProvider} />
                    </Stepper>
                </div>
            ) : null }
            {
                confirmEmail ? 
                    <EmailConfirm signUpCompleteUrl={loginUrl} apiUrl={apiUrl} apiProtocol={apiProtocol}/> 
                    : 
                    <Login signUpCompleteUrl={loginUrl} />
            }
        </React.Fragment>
    );

    const footer = (
        <PageHeightAdjuster>
            <div className={classes.Bar + ' ' + classes.Footer}>
                <PartnerizeLogo width={100} />
                {l('Made by')}
                {' '}
                <Link
                    id="home-page-link"
                    href={`http://www.partnerize.com/${WepPageSupportedLanguages.includes(locale) ? locale : 'en'}`}
                    target="_blank"
                >
                    Partnerize
                </Link>
            </div>
        </PageHeightAdjuster>
    );

    return (
        <div className={classes.SignupForm}>
            <div className={classes.Bar}>
                <LanguageSelector/>
            </div>
            <div className={classes.ContentPanel}>
                {contents}
            </div>
            {footer}
        </div>);
};

const ThemedCustomisation = withTheme(
    makeTheme(Theme)
)(
    withStyles(makeStyle)(Customisation)
);

const ConnectedCustomisation =  connectWithFormName(
    (state, { formName }) => ({
        registrationCompleted: hasSubmitSucceeded(formName)(state),
        captchaProvider: state.application.captchaProvider,
    })
)(ThemedCustomisation);

export {
    ConnectedCustomisation as default, ThemedCustomisation
};